import { useContext, useEffect, useState } from "react";
import { PatientContext } from "../../../../patientFromContext/PatientsContext";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDirectusApi } from "../../../../../../libs/data-access";
import { showToast } from "../../../../../../utils/utility";
import { IELISATests } from "../../../../../../libs/data-access/types";

const DefaultELISATests = {
  h_pylori_elisa: "",
  h_pylori_elisa_why_not_done: "",
  h_pylori_elisa_value: "",
  h_pylori_elisa_result: "",
  s_typhi_elisa: "",
  s_typhi_elisa_why_not_done: "",
  s_typhi_elisa_value: "",
  s_typhi_elisa_result: "",
};

export default function useELISATests() {
  const {
    activeTab,
    setIsBtnLoading,
    isBtnNextClick,
    handleNextTab,
  } = useContext(PatientContext);
  const navigate = useNavigate();
  const location = useLocation();


  const [searchParams] = useSearchParams();
  const patient_id = searchParams.get("patient_id");
  const visit_id = searchParams.get("visit_id");
  const { postDirectData, fetchDirectData, patchDirectData } = useDirectusApi();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [recordId, setRecordId] = useState<string | null>(null);
  const [elisaData, setElisaData] = useState(DefaultELISATests);

  useEffect(() => {
    if (activeTab === "elisa_tests") {
      getELISADetails();
    }
  }, [activeTab]);

  useEffect(() => {
    if (isBtnNextClick && activeTab === "elisa_tests") {
      handleSubmitELISATests();
    }
  }, [isBtnNextClick]);

  const handleChange = (key: string, value: any) => {
    setElisaData((prev) => {
      let updated = { ...prev, [key]: value };
      if (key === "h_pylori_elisa") {
        if (value === "Not done") {
          updated.h_pylori_elisa_value = "";
          updated.h_pylori_elisa_result = "";
        } else if (value === "Done") {
          updated.h_pylori_elisa_why_not_done = "";
        }
      } else if (key === "s_typhi_elisa") {
        if (value === "Not done") {
          updated.s_typhi_elisa_value = "";
          updated.s_typhi_elisa_result = "";
        } else if (value === "Done") {
          updated.s_typhi_elisa_why_not_done = "";
        }
      }
      return updated;
    });
  };

  const getELISADetails = async () => {
    setIsLoading(true);
    const res = await fetchDirectData(`items/elisa_tests?filter[visit_id][_eq]=${visit_id}`);
    if (res.isSuccess && res.data?.length) {
      const data: IELISATests = res.data[0];
      setRecordId(data.id || null);
      setElisaData({
        h_pylori_elisa: data.h_pylori_elisa || "",
        h_pylori_elisa_why_not_done: data.h_pylori_elisa_why_not_done || "",
        h_pylori_elisa_value: data.h_pylori_elisa_value || "",
        h_pylori_elisa_result: data.h_pylori_elisa_result || "",
        s_typhi_elisa: data.s_typhi_elisa || "",
        s_typhi_elisa_why_not_done: data.s_typhi_elisa_why_not_done || "",
        s_typhi_elisa_value: data.s_typhi_elisa_value || "",
        s_typhi_elisa_result: data.s_typhi_elisa_result || "",
      });
    }
    setIsLoading(false);
  };

  const handleSubmitELISATests = async () => {
    if (!patient_id || !visit_id) return;
    setIsBtnLoading(true);

    const payload = {
      ...elisaData,
      user_id: patient_id,
      visit_id: visit_id,
    };

    if (recordId) {
      const res = await patchDirectData(`items/elisa_tests/${recordId}`, payload);
      if (res.isSuccess) {
        showToast({
          type: "success",
          title: "Updated successfully",
          message: "ELISA test results updated",
        });
        navigate(`/index/patient/details?patient_id=${patient_id}`);
      } else {
        showToast({
          type: "error",
          message: res?.message || "Failed to update ELISA tests",
        });
      }
    } else {
      const res = await postDirectData(`items/elisa_tests`, payload);
      if (res.isSuccess) {
        setRecordId(res.data.id || null);
        showToast({
          type: "success",
          title: "Saved successfully",
          message: "ELISA test results saved",
        });
        if (location?.pathname.includes("new-visit")) {
          navigate(`/index/patient/details?patient_id=${patient_id}`);
        } else {
          navigate(`/index`);
        }
      } else {
        showToast({
          type: "error",
          message: res?.message || "Failed to save ELISA tests",
        });
      }
    }
    setIsBtnLoading(false);
  };

  return {
    isLoading,
    elisaData,
    handleChange,
  };
}
